// src/screens/StreamsScreen.js
// ============================================================================
// STREAMS — Twitch/Kick-style browse surface for personal streaming
// (founder-approved mockup, 2026-07-09).
//
// - Lists everyone live on the relay right now (listLiveStreams), sorted by
//   viewers. Game filter chips + "In a match" chip + client-side search.
// - Left "Live now" rail on wide screens (Twitch pattern).
// - Cards: uploaded thumbnail (YouTube model) or flat game-art fallback,
//   LIVE badge, watching count, duration, title/streamer/game, gold
//   "In a match" tag. In-match cards deep-link to the EVENT watch page
//   (prize context); others go to the personal StreamWatch page.
// - Signed-in streamers get a "Your channel" card: stream info editor
//   (title / game / thumbnail upload, 2MB cap) + link to their stream key.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Modal,
  Pressable,
  Image,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import SquadManagePanel, { useTeamStreamsVisible, SquadLinkPrompt } from '../components/SquadManagePanel';
import GameTilePicker from '../components/GameTilePicker';
import StreamScheduleEditor from '../components/StreamScheduleEditor'; // evergreen Step 1.2
import { streamGameName } from '../constants/streamGames';
import { handOffStreamCard } from '../utils/watchCardHandoff';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  LIVE: '#e02222',
};

// Flat fallback tints for cards without an uploaded thumbnail.
const GAME_TINTS = {
  mdn: '#16233a', cod: '#332119', gt7: '#182f25', sf6: '#2e1a33', mvr: '#40202a',
};
const DEFAULT_TINT = '#1c1c1c';

// Names resolve across BOTH catalogs (GAME_PROFILES + browse-only extras).
const gameName = (key) => (key ? streamGameName(key) : null);

// Display name for a stream's game — 'other' shows the streamer's own text.
const streamGameLabel = (s) => (s.game === 'other' && s.gameCustom ? s.gameCustom : gameName(s.game));

const fmtViewers = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));

const fmtDuration = (startedAtMs, nowMs) => {
  if (!startedAtMs) return '';
  const s = Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const initials = (name) => (name || '??').replace(/[^a-zA-Z0-9 ]/g, '').split(' ')
  .map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '??';

export default function StreamsScreen({ navigation }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const { width } = useWindowDimensions();
  const showRail = width >= 900;

  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gameFilter, setGameFilter] = useState('all'); // 'all' | gameKey | 'inmatch'
  const [search, setSearch] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());

  // Your-channel editor state (signed-in only)
  const isLoggedIn = !!getAuth().currentUser;
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [myInfo, setMyInfo] = useState({ title: '', game: '', gameCustom: '', thumbnailUrl: null });
  const [savingInfo, setSavingInfo] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  // Own public handle — the /s/ watch URL segment (public_profiles.userName is
  // canonical, users.userName fallback; NEVER displayName on a public surface).
  const [myHandle, setMyHandle] = useState(null);

  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    let alive = true;
    (async () => {
      try {
        const pub = await getDoc(doc(db, 'public_profiles', uid));
        if (alive && pub.exists() && pub.data().userName) { setMyHandle(pub.data().userName); return; }
        const u = await getDoc(doc(db, 'users', uid));
        if (alive && u.exists() && u.data().userName) setMyHandle(u.data().userName);
      } catch (_) { /* uid fallback still works */ }
    })();
    return () => { alive = false; };
  }, []);

  // The watch page renders your channel live OR offline (Twitch/YouTube
  // "go to my channel" — founder 2026-07-23).
  const openMyChannel = () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    setShowChannelModal(false);
    navigation.navigate('StreamWatch', { channel: myHandle || uid });
  };

  // Chat-only popout window: watch your own chat without pulling the stream
  // (bandwidth). The same /chat/<handle> URL drops into an OBS Custom Browser
  // Dock (founder 2026-07-23).
  const openMyChatPopout = () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid || Platform.OS !== 'web' || typeof window === 'undefined') return;
    window.open(
      `/chat/${encodeURIComponent(myHandle || uid)}`,
      'cc_chat_popout',
      'width=400,height=680,noopener'
    );
  };

  // ---- Team Streams v1 (flag-gated via settings/streams.teamStreamsEnabled).
  // With the flag OFF (and no admin signed in) NONE of the squad UI renders —
  // the screen stays byte-for-byte identical to the pre-squads version.
  // Admins are exempt from the flag (matches the backend callable gates) so
  // squads can be exercised in prod while the feature ships dark. The squad
  // MANAGE UI (opt-in toggle, invites, create form, rosters) lives in
  // SquadManagePanel — shared with the Streaming Hub.
  const teamVisible = useTeamStreamsVisible();
  const [squads, setSquads] = useState([]); // listActiveTeamStreams cards
  const [showSquadModal, setShowSquadModal] = useState(false);

  const fetchStreams = useCallback(async () => {
    try {
      // Streamers directory: LIVE first, then OFFLINE channels (founder
      // 2026-07-25) so the page is never empty at launch.
      const fn = httpsCallable(functions, 'listStreamers');
      const result = await fn();
      setStreams(result.data?.streamers || []);
    } catch (err) {
      console.warn('[StreamsScreen] list failed:', err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [functions]);

  // Poll while focused; tick durations every second.
  useEffect(() => {
    if (!isFocused) return;
    fetchStreams();
    const poll = setInterval(fetchStreams, 45000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [isFocused, fetchStreams]);

  const onRefresh = () => { setRefreshing(true); fetchStreams(); };

  const openStream = (s) => {
    if (s.inMatch?.eventId) {
      navigation.navigate('EventWatch', { eventId: s.inMatch.eventId });
    } else {
      // Watch URLs carry the public handle, never the uid (leak — 2026-07-23).
      // The card's picture goes over in memory, not in the URL (10-15).
      handOffStreamCard(s.userName || s.userId, s);
      navigation.navigate('StreamWatch', { channel: s.userName || s.userId });
    }
  };

  // ---- Your channel editor --------------------------------------------------

  const openChannelModal = async () => {
    setShowChannelModal(true);
    try {
      const fn = httpsCallable(functions, 'getMyStreamInfo');
      const result = await fn();
      setMyInfo({
        title: result.data?.title || '',
        game: result.data?.game || '',
        gameCustom: result.data?.gameCustom || '',
        thumbnailUrl: result.data?.thumbnailUrl || null,
        schedule: result.data?.schedule || null,
        nextStreamAtMs: result.data?.nextStreamAtMs || null,
      });
    } catch (_) { /* defaults are fine */ }
  };

  const saveChannelInfo = async () => {
    try {
      setSavingInfo(true);
      const fn = httpsCallable(functions, 'setMyStreamInfo');
      await fn({ title: myInfo.title, game: myInfo.game || null, gameCustom: myInfo.gameCustom || null });
      setShowChannelModal(false);
      fetchStreams();
    } catch (err) {
      showError('Could not save stream info', err.message || 'Please try again.');
    } finally {
      setSavingInfo(false);
    }
  };

  const pickThumbnail = async () => {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      showError('Permission required', 'Allow photo access to upload a thumbnail.');
      return;
    }
    const picked = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [16, 9],
      quality: 0.6,
      base64: true,
    });
    if (picked.canceled) return;

    try {
      setUploadingThumb(true);
      const asset = picked.assets[0];
      let imageBase64 = asset.base64;
      if (!imageBase64) {
        const blob = await (await fetch(asset.uri)).blob();
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      const fn = httpsCallable(functions, 'uploadStreamThumbnail');
      const { data } = await fn({ imageBase64, contentType: 'image/jpeg' });
      setMyInfo((prev) => ({ ...prev, thumbnailUrl: data?.url || prev.thumbnailUrl }));
    } catch (err) {
      showError('Thumbnail upload failed', err.message || 'Keep it under 2MB (1280×720 recommended).');
    } finally {
      setUploadingThumb(false);
    }
  };

  // ---- Team Streams v1 (squads) ---------------------------------------------

  const fetchSquads = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'listActiveTeamStreams');
      const result = await fn();
      setSquads(result.data?.teamStreams || result.data?.squads || []);
    } catch (_) { /* squads section is best-effort */ }
  }, [functions]);

  useEffect(() => {
    if (!isFocused || !teamVisible) return;
    fetchSquads();
    const poll = setInterval(fetchSquads, 45000);
    return () => clearInterval(poll);
  }, [isFocused, teamVisible, fetchSquads]);

  // The panel fetches fresh squads on mount and its form state dies with
  // unmount, so opening the modal is always fresh — no reset/fetch here.
  const openSquadModal = () => {
    if (!getAuth().currentUser) { navigation.navigate('SignUp'); return; }
    setShowSquadModal(true);
  };

  // Streamer handle → public profile (founder 2026-07-10). displayName on this
  // surface IS the public userName; the placeholder has no profile.
  const openProfile = (userName) => {
    if (!userName || userName === 'CritCash streamer') return;
    navigation.navigate('PublicProfile', { userName });
  };

  // ---- Filtering ------------------------------------------------------------

  const liveGames = [...new Set(streams.map((s) => s.game).filter(Boolean))];
  const filtered = streams.filter((s) => {
    if (gameFilter === 'inmatch' && !s.inMatch) return false;
    if (gameFilter !== 'all' && gameFilter !== 'inmatch' && s.game !== gameFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${s.displayName || ''} ${s.title || ''} ${streamGameLabel(s) || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ---- Renders ---------------------------------------------------------------

  const renderAvatar = (s, size = 32) => (
    s.avatarUrl ? (
      <Image source={{ uri: s.avatarUrl }} style={{ width: size, height: size, borderRadius: size / 2 }} />
    ) : (
      <View style={[styles.avatarFallback, { width: size, height: size, borderRadius: size / 2 }]}>
        <Text style={styles.avatarFallbackText}>{initials(s.displayName)}</Text>
      </View>
    )
  );

  const renderRail = () => (
    <View style={styles.rail}>
      <Text style={styles.railTitle}>LIVE NOW</Text>
      {streams.filter((s) => s.live).slice(0, 10).map((s) => (
        <TouchableOpacity key={`rail-${s.userId}`} style={styles.railRow} onPress={() => openStream(s)}>
          {renderAvatar(s, 28)}
          <View style={{ flex: 1, minWidth: 0, marginHorizontal: 8 }}>
            <Text style={styles.railName} numberOfLines={1}>{s.displayName}</Text>
            <Text style={styles.railGame} numberOfLines={1}>{streamGameLabel(s) || 'Streaming'}</Text>
          </View>
          <Text style={styles.railViewers}>{fmtViewers(s.viewers)}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const fmtAgo = (ms) => {
    const mins = Math.floor(Math.max(0, nowMs - ms) / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
  };

  // Offline streamer card → channel page (/u/). Live cards → the watch page.
  const renderOfflineCard = (s) => (
    <TouchableOpacity key={s.userId} style={styles.card} onPress={() => openProfile(s.userName)} activeOpacity={0.85}>
      <View style={[styles.thumb, { backgroundColor: GAME_TINTS[s.game] || DEFAULT_TINT }]}>
        {s.thumbnailUrl ? (
          <Image source={{ uri: s.thumbnailUrl }} style={styles.thumbImage} resizeMode="cover" />
        ) : (
          <Text style={styles.thumbWatermark}>{(s.game || 'CHANNEL').toUpperCase()}</Text>
        )}
        <View style={styles.offlineBadge}><Text style={styles.offlineBadgeText}>OFFLINE</Text></View>
        {s.lastLiveMs ? (
          <View style={styles.durationBadge}>
            <Text style={styles.viewerBadgeText}>Last live {fmtAgo(s.lastLiveMs)}</Text>
          </View>
        ) : null}
      </View>
      <View style={styles.cardMeta}>
        {renderAvatar(s, 32)}
        <View style={{ flex: 1, minWidth: 0, marginLeft: 9 }}>
          <Text style={styles.cardTitle} numberOfLines={1}>{s.displayName}</Text>
          <Text style={styles.cardStreamer} numberOfLines={1}>
            {s.followerCount ? `${fmtViewers(s.followerCount)} follower${s.followerCount === 1 ? '' : 's'}` : 'View channel ›'}
          </Text>
          <View style={styles.cardChips}>
            {s.game ? <View style={styles.gameChip}><Text style={styles.gameChipText}>{streamGameLabel(s)}</Text></View> : null}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderCard = (s) => {
    if (!s.live) return renderOfflineCard(s);
    return (
    <TouchableOpacity key={s.userId} style={styles.card} onPress={() => openStream(s)} activeOpacity={0.85}>
      <View style={[styles.thumb, { backgroundColor: GAME_TINTS[s.game] || DEFAULT_TINT }]}>
        {s.thumbnailUrl ? (
          <Image source={{ uri: s.thumbnailUrl }} style={styles.thumbImage} resizeMode="cover" />
        ) : (
          <Text style={styles.thumbWatermark}>{(s.game || 'LIVE').toUpperCase()}</Text>
        )}
        <View style={styles.liveBadge}><Text style={styles.liveBadgeText}>LIVE</Text></View>
        <View style={styles.viewerBadge}>
          <Text style={styles.viewerBadgeText}>{fmtViewers(s.viewers)} watching</Text>
        </View>
        {s.startedAtMs ? (
          <View style={styles.durationBadge}>
            <Text style={styles.viewerBadgeText}>{fmtDuration(s.startedAtMs, nowMs)}</Text>
          </View>
        ) : null}
      </View>
      <View style={styles.cardMeta}>
        {renderAvatar(s, 32)}
        <View style={{ flex: 1, minWidth: 0, marginLeft: 9 }}>
          <Text style={styles.cardTitle} numberOfLines={1}>{s.title || `${s.displayName} is live`}</Text>
          <Text
            style={styles.cardStreamer}
            numberOfLines={1}
            onPress={() => openProfile(s.displayName)}
          >
            {s.displayName}
          </Text>
          <View style={styles.cardChips}>
            {s.game ? <View style={styles.gameChip}><Text style={styles.gameChipText}>{streamGameLabel(s)}</Text></View> : null}
            {s.inMatch ? <View style={styles.matchChip}><Text style={styles.matchChipText}>In a match</Text></View> : null}
          </View>
        </View>
      </View>
    </TouchableOpacity>
    );
  };

  const renderChannelModal = () => (
    <Modal visible={showChannelModal} transparent animationType="fade" onRequestClose={() => setShowChannelModal(false)}>
      <Pressable style={styles.modalBackdrop} onPress={() => setShowChannelModal(false)}>
        <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>Your Channel</Text>

          <Text style={styles.fieldLabel}>Stream title</Text>
          <TextInput
            style={styles.textInput}
            value={myInfo.title}
            onChangeText={(t) => setMyInfo((p) => ({ ...p, title: t }))}
            placeholder="What are you playing today?"
            placeholderTextColor={COLORS.DIM}
            maxLength={120}
          />

          <Text style={styles.fieldLabel}>Game</Text>
          <GameTilePicker
            game={myInfo.game}
            gameCustom={myInfo.gameCustom}
            onChange={({ game, gameCustom }) => setMyInfo((p) => ({ ...p, game, gameCustom }))}
          />

          <Text style={styles.fieldLabel}>Thumbnail</Text>
          <TouchableOpacity style={styles.thumbPicker} onPress={pickThumbnail} disabled={uploadingThumb}>
            {myInfo.thumbnailUrl ? (
              <Image source={{ uri: myInfo.thumbnailUrl }} style={styles.thumbPickerImage} resizeMode="cover" />
            ) : (
              <Ionicons name="image-outline" size={28} color={COLORS.DIM} />
            )}
            <Text style={styles.thumbPickerHint}>
              {uploadingThumb ? 'Uploading…' : 'Tap to upload — 1280×720 recommended, 2MB max'}
            </Text>
          </TouchableOpacity>

          <StreamScheduleEditor
            compact
            schedule={myInfo.schedule || null}
            nextStreamAtMs={myInfo.nextStreamAtMs || null}
            showError={showError}
            onSaved={(d) => setMyInfo((p) => ({ ...p, schedule: d?.schedule || null, nextStreamAtMs: d?.nextStreamAtMs || null }))}
          />
          <TouchableOpacity style={styles.keyLink} onPress={openMyChannel}>
            <Ionicons name="tv-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 6 }} />
            <Text style={styles.keyLinkText}>Go to your channel page</Text>
          </TouchableOpacity>

          {/* Chat-only popout — no player, no stream bandwidth. Same URL works
              as an OBS Custom Browser Dock (Docks → Custom Browser Docks). */}
          <TouchableOpacity style={styles.keyLink} onPress={openMyChatPopout}>
            <Ionicons name="chatbubbles-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 6 }} />
            <Text style={styles.keyLinkText}>Pop out your chat (works as an OBS dock)</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.keyLink}
            onPress={() => { setShowChannelModal(false); navigation.navigate('StreamingAccounts'); }}
          >
            <Ionicons name="key-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 6 }} />
            <Text style={styles.keyLinkText}>Get your stream key and RTMP settings</Text>
          </TouchableOpacity>

          {/* Team Streams v1 — invisible unless the flag is on (or admin) */}
          {teamVisible ? (
            <TouchableOpacity
              style={styles.keyLink}
              onPress={() => { setShowChannelModal(false); openSquadModal(); }}
            >
              <Ionicons name="people-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 6 }} />
              <Text style={styles.keyLinkText}>Squad streaming - stream together with your team</Text>
            </TouchableOpacity>
          ) : null}

          <View style={styles.modalButtons}>
            <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowChannelModal(false)}>
              <Text style={styles.modalCancelText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalConfirmBtn} onPress={saveChannelInfo} disabled={savingInfo}>
              <Text style={styles.modalConfirmText}>{savingInfo ? 'Saving…' : 'Save'}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ---- Team Streams v1 renders (flag-gated) ---------------------------------

  // Browse card: names + viewer counts ONLY (copy doctrine — no money copy on
  // browse surfaces).
  const renderSquadCard = (sq) => {
    const cardMembers = sq.liveMembers || sq.members || [];
    const viewers = sq.totalViewers ?? sq.viewers ?? 0;
    const memberCount = sq.memberCount ?? cardMembers.length;
    return (
      <TouchableOpacity
        key={sq.teamStreamId}
        style={styles.squadCard}
        onPress={() => navigation.navigate('TeamStream', { teamStreamId: sq.teamStreamId })}
        activeOpacity={0.85}
      >
        <View style={styles.squadAvatarStack}>
          {cardMembers.slice(0, 4).map((m, i) => (
            <View key={m.uid || m.userName || i} style={[styles.squadAvatarWrap, { marginLeft: i === 0 ? 0 : -10 }]}>
              {m.avatarUrl ? (
                <Image source={{ uri: m.avatarUrl }} style={styles.squadAvatar} />
              ) : (
                <View style={[styles.squadAvatar, styles.avatarFallback]}>
                  <Text style={styles.avatarFallbackText}>{initials(m.userName)}</Text>
                </View>
              )}
            </View>
          ))}
          {memberCount > 4 ? (
            <View style={[styles.squadAvatarWrap, styles.squadAvatarMore, { marginLeft: -10 }]}>
              <Text style={styles.squadAvatarMoreText}>+{memberCount - 4}</Text>
            </View>
          ) : null}
        </View>
        <Text style={styles.squadCardName} numberOfLines={1}>{sq.name || 'Squad'}</Text>
        <View style={styles.squadCardMetaRow}>
          <View style={styles.liveDot} />
          <Text style={styles.squadCardMeta}>
            {fmtViewers(viewers)} watching  |  {memberCount} member{memberCount === 1 ? '' : 's'}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderSquadModal = () => (
    <Modal visible={showSquadModal} transparent animationType="fade" onRequestClose={() => setShowSquadModal(false)}>
      <Pressable style={styles.modalBackdrop} onPress={() => setShowSquadModal(false)}>
        <Pressable style={[styles.modalContent, { maxWidth: 480 }]} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>Squad Streaming</Text>
          <ScrollView style={{ maxHeight: 520 }} showsVerticalScrollIndicator={false}>
            {/* Shared squad manage UI (also on the Streaming Hub). Mounted only
                while the modal is open so every open fetches fresh squads and
                closing discards in-progress form state. */}
            {showSquadModal ? (
              <SquadManagePanel
                navigation={navigation}
                onNavigate={(id) => { setShowSquadModal(false); navigation.navigate('TeamStream', { teamStreamId: id }); }}
                onSquadsChanged={fetchSquads}
              />
            ) : null}
          </ScrollView>

          <TouchableOpacity style={[styles.modalCancelBtn, { marginTop: 12 }]} onPress={() => setShowSquadModal(false)}>
            <Text style={styles.modalCancelText}>Close</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.GOLD} />}
      >
        {/* Title row */}
        <View style={styles.titleRow}>
          <Text style={styles.screenTitle}>Streamers</Text>
          <View style={styles.liveCount}>
            <View style={styles.liveDot} />
            <Text style={styles.liveCountText}>{streams.filter((s) => s.live).length} live</Text>
          </View>
          <View style={{ flex: 1 }} />
          {isLoggedIn && (
            <TouchableOpacity style={styles.goLiveBtn} onPress={openChannelModal}>
              <Ionicons name="radio-outline" size={15} color="#0b0b0b" style={{ marginRight: 6 }} />
              <Text style={styles.goLiveBtnText}>Your channel</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Search */}
        <View style={styles.searchRow}>
          <Ionicons name="search" size={15} color={COLORS.DIM} style={{ marginRight: 8 }} />
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Search streamers"
            placeholderTextColor={COLORS.DIM}
          />
        </View>

        {/* Filter chips */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipRow}>
          <TouchableOpacity
            style={[styles.filterChip, gameFilter === 'all' && styles.filterChipActive]}
            onPress={() => setGameFilter('all')}
          >
            <Text style={[styles.filterChipText, gameFilter === 'all' && styles.filterChipTextActive]}>All</Text>
          </TouchableOpacity>
          {liveGames.map((key) => (
            <TouchableOpacity
              key={key}
              style={[styles.filterChip, gameFilter === key && styles.filterChipActive]}
              onPress={() => setGameFilter(gameFilter === key ? 'all' : key)}
            >
              <Text style={[styles.filterChipText, gameFilter === key && styles.filterChipTextActive]}>
                {gameName(key)}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[styles.filterChip, gameFilter === 'inmatch' && styles.filterChipActive]}
            onPress={() => setGameFilter(gameFilter === 'inmatch' ? 'all' : 'inmatch')}
          >
            <Text style={[styles.filterChipText, gameFilter === 'inmatch' && styles.filterChipTextActive]}>
              ⚔ In a match
            </Text>
          </TouchableOpacity>
        </ScrollView>

        {/* Squad streams (Team Streams v1) — renders ONLY when the flag is on
            (or an admin is signed in) AND at least one squad is live. Browse
            surface carries names + viewer counts only. */}
        {teamVisible && squads.length > 0 ? (
          <View style={styles.squadSection}>
            <Text style={styles.squadSectionTitle}>Squad streams</Text>
            <Text style={styles.squadSectionSub}>Watch the whole crew, support them all at once.</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
              {squads.map(renderSquadCard)}
            </ScrollView>
          </View>
        ) : null}

        {/* Rail + grid */}
        {loading ? (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 40 }} />
        ) : streams.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="videocam-off-outline" size={34} color={COLORS.DIM} />
            <Text style={styles.emptyTitle}>No streamers yet</Text>
            <Text style={styles.emptyBody}>
              Streamers and their channels show up here.
              {isLoggedIn ? ' Grab your stream key and be the first.' : ''}
            </Text>
          </View>
        ) : (
          <View style={styles.bodyRow}>
            {showRail && streams.some((s) => s.live) && renderRail()}
            <View style={styles.grid}>
              {filtered.length === 0 ? (
                <Text style={styles.emptyBody}>No streamers match your filters.</Text>
              ) : (
                filtered.map(renderCard)
              )}
            </View>
          </View>
        )}
      </ScrollView>

      {renderChannelModal()}
      {teamVisible ? renderSquadModal() : null}
      {/* "Link up?" detection - self-gating (flag + auth + something to
          suggest), shows at most once per session. */}
      <SquadLinkPrompt />

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  scrollView: { flex: 1 },
  content: { padding: 16, paddingBottom: 100 },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  screenTitle: { color: '#fff', fontSize: 22, fontWeight: '800', marginRight: 12 },
  liveCount: { flexDirection: 'row', alignItems: 'center' },
  liveDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: COLORS.LIVE, marginRight: 6 },
  liveCountText: { color: '#e05555', fontSize: 12, fontWeight: '700' },
  goLiveBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.GOLD,
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14,
  },
  goLiveBtnText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },

  searchRow: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#141414',
    borderColor: '#2a2a2a', borderWidth: 1, borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: Platform.OS === 'web' ? 8 : 4, marginBottom: 12,
  },
  searchInput: { flex: 1, color: COLORS.TEXT, fontSize: 13, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) },

  chipRow: { marginBottom: 16, flexGrow: 0 },
  filterChip: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 999, paddingVertical: 6, paddingHorizontal: 14, marginRight: 8,
  },
  filterChipActive: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  filterChipText: { color: '#bbb', fontSize: 12 },
  filterChipTextActive: { color: '#412402', fontWeight: '700' },

  bodyRow: { flexDirection: 'row' },
  rail: { width: 170, marginRight: 16 },
  railTitle: { color: COLORS.DIM, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, marginBottom: 10 },
  railRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  railName: { color: '#eee', fontSize: 12, fontWeight: '600' },
  railGame: { color: '#777', fontSize: 11 },
  railViewers: { color: '#e05555', fontSize: 11 },

  grid: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  card: { flexGrow: 1, flexBasis: 240, maxWidth: 420 },
  thumb: {
    aspectRatio: 16 / 9, borderRadius: 8, overflow: 'hidden',
    alignItems: 'center', justifyContent: 'center',
  },
  thumbImage: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' },
  thumbWatermark: { color: 'rgba(255,255,255,0.18)', fontSize: 24, fontWeight: '800', letterSpacing: 3 },
  liveBadge: {
    position: 'absolute', top: 8, left: 8, backgroundColor: COLORS.LIVE,
    borderRadius: 4, paddingVertical: 2, paddingHorizontal: 6,
  },
  liveBadgeText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  offlineBadge: {
    position: 'absolute', top: 8, left: 8, backgroundColor: 'rgba(20,20,20,0.85)',
    borderRadius: 4, paddingVertical: 2, paddingHorizontal: 6,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
  },
  offlineBadgeText: { color: '#bbb', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  viewerBadge: {
    position: 'absolute', bottom: 8, left: 8, backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: 4, paddingVertical: 2, paddingHorizontal: 7,
  },
  durationBadge: {
    position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: 4, paddingVertical: 2, paddingHorizontal: 7,
  },
  viewerBadgeText: { color: '#fff', fontSize: 11 },

  cardMeta: { flexDirection: 'row', marginTop: 9 },
  cardTitle: { color: '#eee', fontSize: 13, fontWeight: '600' },
  cardStreamer: { color: COLORS.DIM, fontSize: 12, marginTop: 1 },
  cardChips: { flexDirection: 'row', marginTop: 5, gap: 5 },
  gameChip: { backgroundColor: '#1c1c1c', borderRadius: 4, paddingVertical: 2, paddingHorizontal: 8, borderWidth: 1, borderColor: '#1c1c1c' },
  gameChipActive: { backgroundColor: '#241d00', borderColor: COLORS.GOLD },
  gameChipText: { color: '#999', fontSize: 10 },
  gameChipTextActive: { color: COLORS.GOLD, fontWeight: '700' },
  matchChip: { backgroundColor: '#241d00', borderRadius: 4, paddingVertical: 2, paddingHorizontal: 8 },
  matchChipText: { color: COLORS.GOLD, fontSize: 10, fontWeight: '700' },

  avatarFallback: { backgroundColor: '#2a3550', alignItems: 'center', justifyContent: 'center' },
  avatarFallbackText: { color: '#b5d4f4', fontSize: 11, fontWeight: '700' },

  emptyState: { alignItems: 'center', paddingVertical: 48 },
  emptyTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700', marginTop: 12 },
  emptyBody: { color: COLORS.DIM, fontSize: 13, textAlign: 'center', marginTop: 6, lineHeight: 19, maxWidth: 360 },

  // Modal
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'center', alignItems: 'center', padding: 16 },
  modalContent: {
    width: '100%', maxWidth: 440, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  modalTitle: { color: '#fff', fontSize: 18, fontWeight: '800', marginBottom: 14 },
  fieldLabel: { color: '#aaa', fontSize: 12, fontWeight: '700', marginBottom: 6, marginTop: 10 },
  textInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1, borderRadius: 8,
    color: COLORS.TEXT, fontSize: 13, paddingVertical: 9, paddingHorizontal: 12,
  },
  modalGameChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  thumbPicker: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1, borderRadius: 8,
    alignItems: 'center', justifyContent: 'center', paddingVertical: 14, overflow: 'hidden',
  },
  thumbPickerImage: { width: '100%', aspectRatio: 16 / 9, borderRadius: 6, marginBottom: 8 },
  thumbPickerHint: { color: COLORS.DIM, fontSize: 11, marginTop: 6, textAlign: 'center', paddingHorizontal: 10 },
  keyLink: { flexDirection: 'row', alignItems: 'center', marginTop: 14 },
  keyLinkText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '600' },
  modalButtons: { flexDirection: 'row', gap: 10, marginTop: 18 },
  modalCancelBtn: {
    flex: 1, borderColor: '#333', borderWidth: 1, borderRadius: 8,
    paddingVertical: 11, alignItems: 'center',
  },
  modalCancelText: { color: '#aaa', fontSize: 13, fontWeight: '600' },
  modalConfirmBtn: { flex: 1, backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 11, alignItems: 'center' },
  modalConfirmText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },

  // ---- Team Streams v1 (flag-gated squads UI) ----
  squadSection: { marginBottom: 16 },
  squadSectionTitle: { color: '#fff', fontSize: 15, fontWeight: '800' },
  squadSectionSub: { color: COLORS.DIM, fontSize: 12, marginTop: 2, marginBottom: 10 },
  squadCard: {
    width: 200, backgroundColor: COLORS.CARD, borderColor: COLORS.CARD_BORDER,
    borderWidth: 1, borderRadius: 10, padding: 12, marginRight: 10,
  },
  squadAvatarStack: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  squadAvatarWrap: {
    borderWidth: 2, borderColor: COLORS.CARD, borderRadius: 16, overflow: 'hidden',
  },
  squadAvatar: { width: 28, height: 28, borderRadius: 14 },
  squadAvatarMore: {
    width: 28, height: 28, borderRadius: 14, backgroundColor: '#1c1c1c',
    alignItems: 'center', justifyContent: 'center',
  },
  squadAvatarMoreText: { color: '#bbb', fontSize: 10, fontWeight: '700' },
  squadCardName: { color: '#eee', fontSize: 13, fontWeight: '700' },
  squadCardMetaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 5 },
  squadCardMeta: { color: COLORS.DIM, fontSize: 11, marginLeft: 2 },
  // (squad MANAGE styles — rows, roster, picks, invite search — moved to
  // components/SquadManagePanel.js with the UI they style)
});
